/**
 * ReleaseNotesFieldPicker — lets owners/admins point a product at the Jira
 * custom field that holds release-notes content.
 */

import { Tag } from 'lucide-react';
import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '../../../../../shared/apiClient';
import {
  useProductBranding,
  useUpdateProductBranding,
} from '../../../../../shared/hooks/useProductBranding';
import { CustomSelect } from '../../../../../shared/components/CustomSelect';

interface CatalogResponse {
  fields: Array<{
    id: number;
    jiraFieldId: string;
    displayName: string;
    fieldKind: string;
  }>;
}

export interface ReleaseNotesFieldPickerProps {
  productId: string | null;
}

export const ReleaseNotesFieldPicker: React.FC<ReleaseNotesFieldPickerProps> = ({ productId }) => {
  const branding = useProductBranding(productId);
  const updateBranding = useUpdateProductBranding();

  const catalog = useQuery<CatalogResponse, Error>({
    queryKey: ['/api/custom-fields/catalog'],
    queryFn: async ({ signal }) => {
      const res = await apiFetch('/api/custom-fields/catalog', { signal });
      if (!res.ok) throw new Error(`API ${res.status}: /api/custom-fields/catalog`);
      return res.json();
    },
    enabled: !!productId,
    staleTime: 60_000,
  });

  const options = useMemo(() => catalog.data?.fields ?? [], [catalog.data]);
  const current = branding.data?.releaseNotesFieldId ?? null;
  const hasFields = options.length > 0;

  const dropdownOptions = useMemo(
    () =>
      options.map((o) => ({
        value: String(o.id),
        label: `${o.displayName} (${o.jiraFieldId})`,
      })),
    [options]
  );

  const suggested = useMemo(() => {
    if (current != null) return null;
    return (
      options.find((o) => o.displayName.toLowerCase() === 'release notes') ||
      options.find((o) => /release\s*notes/i.test(o.displayName)) ||
      null
    );
  }, [options, current]);

  const handleChange = (raw: string) => {
    if (!productId) return;
    const next = raw === '' ? null : Number(raw);
    updateBranding.mutate({ productId, releaseNotesFieldId: next });
  };

  return (
    <>
      <div className="rb-section-header">
        <div className="rb-section-header__icon rb-section-header__icon--neutral">
          <Tag size={18} aria-hidden="true" />
        </div>
        <div className="rb-section-header__text">
          <h2 className="rb-section-header__title">Jira field for Release Notes</h2>
          <p className="rb-section-header__desc">
            Select which Jira custom field contains release-notes content for this product. The
            Reports page renders this field (Atlassian Document Format) when generating Release
            Notes reports.
          </p>
        </div>
      </div>

      {branding.isLoading || catalog.isLoading ? (
        <p style={{ color: 'var(--color-text-secondary)', fontSize: '13px' }}>Loading…</p>
      ) : null}

      {!branding.isLoading && !catalog.isLoading && (
        <div className="form-group">
          <label className="form-label" htmlFor="release-notes-field-select">
            Custom field
          </label>
          <CustomSelect
            id="release-notes-field-select"
            value={current == null ? '' : String(current)}
            options={[{ value: '', label: '— Not configured —' }, ...dropdownOptions]}
            onChange={handleChange}
            disabled={!productId || updateBranding.isPending}
            placeholder="— Not configured —"
          />
          {!hasFields ? (
            <p style={{ fontSize: '12px', color: 'var(--color-text-muted)', marginTop: 4 }}>
              No custom fields registered yet. Configure one in{' '}
              <strong>Custom fields catalog</strong> below, then come back here.
            </p>
          ) : null}
          {suggested ? (
            <p style={{ fontSize: '12px', color: 'var(--color-text-muted)', marginTop: 4 }}>
              Tip: detected a "{suggested.displayName}" field —{' '}
              <button
                type="button"
                className="profile-field__inline-btn"
                onClick={() =>
                  productId &&
                  updateBranding.mutate({ productId, releaseNotesFieldId: suggested.id })
                }
              >
                use it
              </button>
              .
            </p>
          ) : null}
          {updateBranding.isError ? (
            <p style={{ fontSize: '12px', color: 'var(--color-error)', marginTop: 4 }}>
              {(updateBranding.error as Error | null)?.message || 'Save failed'}
            </p>
          ) : null}
        </div>
      )}
    </>
  );
};
